import React from 'react';
import PropTypes from 'prop-types';

import Localizable from 'lib/intl/localizable';
import LoadingSwitch from 'lib/react-router/loading-switch';
import RouteWithSearch from 'lib/react-router/route-with-search';
import LoadingProgress from 'lib/react-router/loading-progress';
import FetchData from 'lib/fetch-data/fetch-data';

import ErrorPage from 'four-nets/errors/error-page';
import { Site } from 'four-nets/site';

import BlogView, { LAYOUT, BlogViewModel } from './blog/blog-view';
import BlogPostDetailView, { BlogPostDetailViewModel } from './blog/blog-post-detail-view';
import BlogPostsView, { BlogPostsViewModel } from './blog/blog-posts-view';
import BlogCommentsView, { BlogCommentsViewModel } from './blog/blog-comments-view';

@Localizable()
class BlogSlugLoader extends React.PureComponent {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
        urlBuilder: PropTypes.object,
        userStore: PropTypes.object,
    };

    state = {
        key: 1,
    };

    categoryFromSlug(slug) {
        const { categories } = this.props;

        if (slug) {
            for (let i = 0; i < categories.length; i++) {
                const currentCategory = categories[i];

                if (currentCategory.slug === slug) {
                    return currentCategory;
                }
            }
        }

        return null;
    }

    transform = (data, { slug, page }) => {
        const { postStore, categories, intl } = this.props;

        const blogViewModel = new BlogViewModel({ categories, postStore, intl });
        blogViewModel.fromJSON(data.blogViewModel);

        if (data.blogPostsViewModel) {
            const category = this.categoryFromSlug(slug);

            const blogPostsViewModel = new BlogPostsViewModel(
                {
                    postStore,
                },
                category,
                page
            );

            blogViewModel.setLayout({
                layout: LAYOUT.category,
            });

            blogViewModel.setActiveCategory({
                activeCategory: category,
            });

            blogPostsViewModel.fromJSON(data.blogPostsViewModel);

            return {
                blogViewModel,
                blogPostsViewModel,
            };
        }

        blogViewModel.setLayout({
            layout: LAYOUT.post,
        });

        const blogPostDetailViewModel = new BlogPostDetailViewModel(
            {
                postStore,
            },
            slug
        );

        blogPostDetailViewModel.fromJSON(data.blogPostDetailViewModel);

        const comments = postStore.commentStore.processPagedJSON({ json: data.comments });

        return {
            blogCommentsViewModel: new BlogCommentsViewModel({
                mailStore: this.context.userStore.mailStore,
                comments,
                urlBuilder: this.context.urlBuilder,
                post: blogPostDetailViewModel.post,
                intl,
            }),
            blogPostDetailViewModel,
            comments,
            blogViewModel,
        };
    };

    fetch = (setData, { page, slug }) => {
        const { categories, postStore, intl } = this.props;

        const blogViewModel = new BlogViewModel({ categories, postStore, intl });

        const category = this.categoryFromSlug(slug);

        if (!slug || category) {
            const blogPostsViewModel = new BlogPostsViewModel(
                {
                    postStore,
                },
                category,
                page
            );

            return Promise.all([blogViewModel.load(), blogPostsViewModel.load()]).then(() => {
                blogViewModel.setLayout({
                    layout: LAYOUT.category,
                });

                blogViewModel.setActiveCategory({
                    activeCategory: category,
                });

                setData(
                    {
                        blogViewModel,
                        blogPostsViewModel,
                    },
                    false
                );
            });
        }

        const blogPostDetailViewModel = new BlogPostDetailViewModel(
            {
                postStore,
            },
            slug
        );

        return Promise.all([blogViewModel.load(), blogPostDetailViewModel.load()]).then(() => {
            const { post } = blogPostDetailViewModel;

            blogViewModel.setLayout({
                layout: LAYOUT.post,
            });

            return post.loadComments().then(comments => {
                setData(
                    {
                        blogCommentsViewModel: new BlogCommentsViewModel({
                            refresh: () => {
                                this.setState(prevState => ({
                                    key: prevState.key + 1,
                                }));
                            },
                            mailStore: this.context.userStore.mailStore,
                            comments,
                            urlBuilder: this.context.urlBuilder,
                            post,
                            intl,
                        }),
                        blogViewModel,
                        blogPostDetailViewModel,
                        comments,
                    },
                    false
                );
            });
        });
    };

    renderFetch = ({ loading, error, data }) => {
        let children;

        if (data) {
            if (data.blogPostsViewModel) {
                children = <BlogPostsView firstMaxi={true} blogPostsViewModel={data.blogPostsViewModel} />;
            } else if (data.blogPostDetailViewModel && data.blogCommentsViewModel) {
                children = (
                    <BlogPostDetailView blogPostDetailViewModel={data.blogPostDetailViewModel}>
                        <BlogCommentsView blogCommentsViewModel={data.blogCommentsViewModel} />
                    </BlogPostDetailView>
                );
            }
        }

        return (
            <LoadingProgress loading={loading}>
                <ErrorPage error={error}>
                    {data ? (
                        <BlogView
                            categoryStore={this.props.categoryStore}
                            postStore={this.props.postStore}
                            categories={this.props.categories}
                            blogViewModel={data.blogViewModel}
                        >
                            {children}
                        </BlogView>
                    ) : null}
                </ErrorPage>
            </LoadingProgress>
        );
    };

    renderRoute = ({ match, search }) => (
        <FetchData
            serializerKey={__filename}
            id={search.id || 0}
            slug={match.params.slug}
            page={search.page || 1}
            render={this.renderFetch}
            transform={this.transform}
            fetch={this.fetch}
        />
    );

    render() {
        return (
            <LoadingSwitch>
                <RouteWithSearch key={this.state.key} path='/magazine/:slug' exact={true} render={this.renderRoute} />
                <RouteWithSearch path='/magazine' exact={true} component={this.renderRoute} />
            </LoadingSwitch>
        );
    }
}
export default BlogSlugLoader;
